
import { defineComponent, onBeforeMount, reactive, watch } from 'vue'
import ymaps from 'yandex-maps'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import NewMap from '@/common/new-components/NewMap/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import ModalPanel from '@/modules/NewDispatcher/components/ModalPanel/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'

// @module-components
import DeliveriesTableRow from '@/modules/NewDispatcher/components/DeliveriesTableRow/index.vue'
import ContainerInvoicePanelInformation from '@/modules/NewDispatcher/components/ContainerInvoicePanelInformation/index.vue'
import CourierPaymentModal from '@/modules/NewDispatcher/components/CourierPaymentModal/index.vue'
import WaitListStatusModal from '@/modules/NewDispatcher/components/WaitListStatusModal/index.vue'
import WaitListMassStatusModal from '@/modules/NewDispatcher/components/WaitListMassStatusModal/index.vue'
import MassApproveModal from '@/modules/NewDispatcher/components/MassApproveModal/index.vue'

// @types
import { DeliveryOrder, GetDeliveryOrdersParams } from '@/new-services/types/delivery'
import { Sector } from '@/new-services/types/sectors'
import { Courier } from '@/new-services/types/couriers'
import { StatusType } from '@/new-services/types/status-type'
import { Comment, TakeOrder, WaitListStatus } from '@/new-services/types/takes'
import { CompanyT } from '@/core/types/companies.types'
import { Container, PlanningRecordInvoice } from '@/new-services/types/planning'

// @new-services
import deliveryAPI from '@/new-services/delivery.service'
import sectorsAPI from '@/new-services/sectors.service'
import couriersAPI from '@/new-services/couriers.service'
import statusTypeAPI from '@/new-services/status-type.service'
import planningAPI from '@/new-services/planning.service'

// @realisations
import { companies } from '@/core/realisations/companies.realisation'
import takeAPI from '@/new-services/takes.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'
import useMap from '@/common/new-composable/useMap'
import useMapGeoObjects from '@/common/new-composable/useMapGeoObjects'
import { useFile } from '@/common/composable/useFile'

// @store
import { useNewDispatcherStore } from '@/modules/NewDispatcher/stores/useNewDispatcherStore'
import { LtlStatusColors } from '@/core/constants/columns/LTL.columns'

const COLUMNS = [
    {
        key: 'lo',
        name: '',
    },
    {
        key: 'index',
        name: '№',
    },
    {
        key: 'dopInvoiceNumber',
        name: 'Номер накладной заказчика',
    },
    {
        name: 'Действия',
        key: 'actions',
    },
    {
        key: 'orderNumber',
        name: 'Номер заказа',
    },
    {
        key: 'invoiceNumber',
        name: 'Номер накладной',
    },
    {
        key: 'status',
        name: 'Статус доставки',
    },
    {
        key: 'containerTitle',
        name: 'Контейнер',
    },
    {
        key: 'waitList',
        name: 'Статус ЛО',
    },
    {
        key: 'waitListComment',
        name: 'Комментарий',
    },
    {
        name: 'Ответ КЦ',
        key: 'waitListId',
    },
    {
        key: 'problems',
        name: 'Проблема',
    },
    {
        key: 'cityName',
        name: 'Город',
        hintText: 'Город получателя',
    },
    {
        key: 'customerSector',
        name: 'Сектор',
    },
    {
        key: 'deliveredAt',
        name: 'Доставлено в',
    },
    {
        key: 'changedTakeDateAt',
        name: 'Дата возврата',
    },
    {
        key: 'createdAt',
        name: 'Дата создания',
    },
    {
        key: 'customerAddress',
        name: 'Адрес получателя',
    },
    {
        key: 'courier',
        name: 'Курьер',
    },
    {
        key: 'companyName',
        name: 'Клиент',
    },
]

const WAIT_LIST_STATUSES = [
    { id: 16, name: 'Перенос даты' },
    { id: 24, name: 'Неточный адрес' },
    { id: 26, name: 'Отсутствие получателя' },
    { id: 27, name: 'Переадресация' },
    { id: 29, name: 'Возврат отправителю' },
    { id: 30, name: 'Отложена по просьбе получателя' },
    { id: 31, name: 'Поломка авто' },
    { id: 32, name: 'Не успел по графику клиента' },
    { id: 33, name: 'Получатель на звонки не отвечает (отключен)' },
]

import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { couriers } from '@/core/realisations/couriers.realisation'
import { useTablesStore } from '@/modules/TablesModule/store/useTablesStore'
import { InvoiceT } from '@/core/types/orders.types'
import { computed } from 'vue'
import useAccount from '@/common/composable/useAccount'

import { PICKUP_OPTIONS } from '@/core/constants/invoices.constants'

export default defineComponent({
    components: {
        PageTitle,
        NewButton,
        NewInput,
        NewSelect,
        NewLimit,
        NewPagination,
        Loader,
        NewMap,
        NewTable,
        DeliveriesTableRow,
        ModalPanel,
        ContainerInvoicePanelInformation,
        CourierPaymentModal,
        WaitListStatusModal,
        WaitListMassStatusModal,
        TableScrollWrapper,
        MassApproveModal,
    },
    setup() {
        const { saveFile } = useFile()
        const new_dispatcher_store = useNewDispatcherStore()
        const notifications = useNotifications()
        const tablesStore = useTablesStore()
        const { havePermissions } = useAccount()

        const { clearMap, addGeoObject, setMapCenter } = useMap()
        const map_graphics = useMapGeoObjects()

        let map_instance = null as ymaps.Map | null

        const state = reactive({
            is_other: false,
            is_opened: true,
            is_map_ready: false,
        })

        const comments_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as Comment[],
        })

        const wait_list_state = reactive({
            is_opened: false,
            is_loading: false,
            order: null as TakeOrder | null,
        })

        const wait_list_mass_state = reactive({
            is_opened: false,
            is_loading: false,
        })

        const mass_state = reactive({
            is_opened: false,
            is_loading: false,
        })

        const invoice_state = reactive({
            is_opened: false,
            is_loading: false,
            invoice: null as PlanningRecordInvoice | null,
            invoice_status: null as { code: number; id: number; name: string } | null,
        })

        const filters_state = reactive({
            invoiceNumber: null as string | null,
            dopInvoiceNumber: null as string | null,
            deliveredAtFrom: null as string | null,
            deliveredAtTo: null as string | null,
            courierId: null as number | null,
            sectorId: null as number | null,
            waitListStatusIds: [] as number[],
            statusIds: [] as number[],
            address: null as string | null,
            companyId: null as number | null,
            containerId: null as number | null,
            waitListComment: null as string | null,
            isPickup: null as string | null,
        })

        const filters$ = useVuelidate(
            {
                deliveredAtFrom: { required },
                deliveredAtTo: { required },
            },
            filters_state
        )

        const deliveries_state = reactive({
            is_loading: false,
            is_failed: false,
            orders: [] as DeliveryOrder[],
            selected_orders: [] as number[],
            filters: {
                limit: 50,
                current_page: 1,
                total_pages: 1,
                total_items: 20,
            },
        })

        const deliveries_map_state = reactive({
            is_loading: false,
            is_failed: false,
            orders: [] as DeliveryOrder[],
        })

        const report_state = reactive({
            is_loading: false,
        })

        const payment_state = reactive({
            is_opened: false,
            is_loading: false,
            type: null as number | null,
            clientId: null as number | null,
        })

        const data_state = reactive({
            sectors: {
                is_loading: false,
                is_failed: false,
                list: [] as Sector[],
            },
            couriers: {
                is_loading: false,
                is_failed: false,
                list: [] as Courier[],
            },
            status_types: {
                is_loading: false,
                is_failed: false,
                list: [] as StatusType[],
            },
            statuses_lo: {
                is_loading: false,
                is_failed: false,
                list: [] as WaitListStatus[],
            },
            companies: {
                is_loading: false,
                is_failed: false,
                list: [] as CompanyT[],
            },
            containers: {
                is_loading: false,
                is_failed: false,
                list: [] as Container[],
            },
        })

        const openInvoicePanel = (invoice: PlanningRecordInvoice) => {
            invoice_state.invoice = { ...invoice }
            invoice_state.is_opened = true
        }

        const onInvoiceLoaded = (invoice: InvoiceT) => {
            invoice_state.invoice_status = invoice.status
        }

        const closeInvoicePanel = () => {
            invoice_state.is_opened = false
            invoice_state.invoice = null
            invoice_state.invoice_status = null
        }

        const editDefaultRecords = () => {
            if (!deliveries_state.orders.length) return

            deliveries_state.orders.forEach((order) => {
                if (order.problems && order.problems.length) {
                    order.isRed = true
                } else {
                    order.isRed = false
                }
            })
        }

        const loadMapDeliveries = async () => {
            try {
                deliveries_map_state.is_loading = true

                let dispatcherSectorIds: number[] = []

                if (new_dispatcher_store.dispatcherSectorIds.length) {
                    dispatcherSectorIds = new_dispatcher_store.dispatcherSectorIds
                }

                const waitListComment = state.is_other ? 'other' : filters_state.waitListComment

                let isPickup = null as number | null

                if (filters_state.isPickup) {
                    if (filters_state.isPickup === '0') {
                        isPickup = 0
                    }

                    if (filters_state.isPickup === '1') {
                        isPickup = 1
                    }
                }

                const params: GetDeliveryOrdersParams = {
                    page: deliveries_state.filters.current_page,
                    dispatcherSectorIds,
                    invoiceNumber: filters_state.invoiceNumber,
                    dopInvoiceNumber: filters_state.dopInvoiceNumber,
                    courierId: filters_state.courierId,
                    sectorId: filters_state.sectorId,
                    createdAtFrom: filters_state.deliveredAtFrom,
                    createdAtTo: filters_state.deliveredAtTo,
                    statusIds: filters_state.statusIds,
                    waitListStatusIds: filters_state.waitListStatusIds,
                    companyId: filters_state.companyId,
                    address: filters_state.address,
                    containerId: filters_state.containerId,
                    isPickup,
                    waitListComment,
                    limit: 9999,
                }

                const response = await deliveryAPI.getDeliveryOrders(params)
                deliveries_map_state.orders = response.data.data

                renderOrders(response.data.data)

                deliveries_map_state.is_failed = false
            } catch (error) {
                console.error(error)
                deliveries_map_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка доставок произошла ошибка',
                })
            } finally {
                deliveries_map_state.is_loading = false
            }
        }

        const loadDeliveries = async () => {
            try {
                deliveries_state.is_loading = true

                let dispatcherSectorIds: number[] = []

                if (new_dispatcher_store.dispatcherSectorIds.length) {
                    dispatcherSectorIds = new_dispatcher_store.dispatcherSectorIds
                }

                const waitListComment = state.is_other ? 'other' : filters_state.waitListComment

                let isPickup = null as number | null

                if (filters_state.isPickup) {
                    if (filters_state.isPickup === '0') {
                        isPickup = 0
                    }

                    if (filters_state.isPickup === '1') {
                        isPickup = 1
                    }
                }

                const params: GetDeliveryOrdersParams = {
                    page: deliveries_state.filters.current_page,
                    dispatcherSectorIds,
                    invoiceNumber: filters_state.invoiceNumber,
                    dopInvoiceNumber: filters_state.dopInvoiceNumber,
                    courierId: filters_state.courierId,
                    sectorId: filters_state.sectorId,
                    createdAtFrom: filters_state.deliveredAtFrom,
                    createdAtTo: filters_state.deliveredAtTo,
                    statusIds: filters_state.statusIds,
                    waitListStatusIds: filters_state.waitListStatusIds,
                    companyId: filters_state.companyId,
                    address: filters_state.address,
                    containerId: filters_state.containerId,
                    isPickup,
                    waitListComment,
                }

                const response = await deliveryAPI.getDeliveryOrders(params)
                deliveries_state.orders = response.data.data
                editDefaultRecords()
                deliveries_state.filters.total_pages = response.data.meta.last_page
                deliveries_state.filters.total_items = response.data.meta.total

                deliveries_state.is_failed = false
            } catch (error) {
                deliveries_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка доставок произошла ошибка',
                })
            } finally {
                deliveries_state.is_loading = false
            }
        }

        const loadData = () => {
            new_dispatcher_store.validation$.$reset()

            resetFilters(false)
            clearMap(map_instance as ymaps.Map)

            loadSectors()
            loadCouriers()
            loadDeliveries()
            loadMapDeliveries()

            new_dispatcher_store.dispatcherSectors.forEach((sector) => {
                if (sector.city && sector.city.latitude && sector.city.longitude) {
                    setMapCenter(map_instance as ymaps.Map, [
                        sector.city.latitude,
                        sector.city.longitude,
                    ])
                } else {
                    const centroid = map_graphics.getPolygonCentroid(JSON.parse(sector.coordinates))

                    setMapCenter(map_instance as ymaps.Map, centroid)
                }
            })
        }

        const startWaitList = (order: TakeOrder) => {
            wait_list_state.order = { ...order }
            wait_list_state.is_opened = true
        }

        const cancelWaitList = () => {
            wait_list_state.is_opened = false
            wait_list_state.order = null
        }

        const startMassApprove = () => {
            mass_state.is_opened = true
        }

        const cancelMassApprove = () => {
            mass_state.is_opened = false
        }

        const setWaitListStatus = async () => {
            cancelWaitList()
            closeInvoicePanel()
            loadData()
        }

        const onLimitChange = (new_limit: number) => {
            deliveries_state.filters.limit = new_limit
            onFiltersChanged()
        }

        const onChangePage = (new_page: number) => {
            deliveries_state.filters.current_page = new_page
            onFiltersChanged()
        }

        const onFiltersChanged = () => {
            filters$.value.$touch()
            if (filters$.value.$invalid) return

            clearMap(map_instance as ymaps.Map)
            onUnselectAll()

            loadDeliveries()
            loadMapDeliveries()
            drawSectors(data_state.sectors.list)
        }

        const resetFilters = (with_reload = true) => {
            if (with_reload) {
                filters_state.invoiceNumber = null
                filters_state.dopInvoiceNumber = null
                filters_state.courierId = null
                filters_state.sectorId = null
                filters_state.waitListStatusIds = []
                filters_state.statusIds = []
                filters_state.address = null
                filters_state.companyId = null
                filters_state.containerId = null
                filters_state.waitListComment = null
                filters_state.isPickup = null
                state.is_other = false

                deliveries_state.filters.limit = 50
                deliveries_state.filters.current_page = 1

                setTodayDate()
                filters$.value.$reset()

                onFiltersChanged()
            }
        }

        const closeMap = () => {
            state.is_opened = false
        }

        const openMap = () => {
            state.is_opened = true
        }

        function createSingleOrderLayout(balloonContentHeader: string, balloonContentBody: string) {
            const SingleOrderLayout = window.ymaps.templateLayoutFactory.createClass(
                `
                    ${balloonContentHeader}
                    ${balloonContentBody}
                `,
                {
                    build: function () {
                        // @ts-ignore
                        SingleOrderLayout.superclass.build.call(this)
                    },
                    clear: function () {
                        // @ts-ignore
                        SingleOrderLayout.superclass.clear.call(this)
                    },
                }
            )
            return SingleOrderLayout
        }

        const renderOrders = (orders: DeliveryOrder[]) => {
            const cluster = map_graphics.createCluster({
                preset: 'islands#invertedYellowClusterIcons',
                gridSize: 80,
                maxZoom: 21,
                // clusterBalloonContentLayoutHeight: 250,
                // clusterBalloonItemContentLayout: createUndeliveredOrderItemLayout(),
            })

            const placemarks = [] as ymaps.Placemark[]

            orders.forEach((delivery_order) => {
                const coordinates = [
                    Number(delivery_order.latitude),
                    Number(delivery_order.longitude),
                ]
                const address = delivery_order.customer
                    ? delivery_order.customer.address
                    : 'Не указан'
                const courier = delivery_order.courier
                    ? delivery_order.courier.fullName
                    : 'Не указан'

                const status = delivery_order.status ? delivery_order.status.title : 'Не указан'
                let statusClass = null as string | null

                if (
                    delivery_order.status.id === 22 ||
                    delivery_order.status.id === 24 ||
                    delivery_order.status.id === 26 ||
                    delivery_order.status.id === 27 ||
                    delivery_order.status.id === 28
                ) {
                    statusClass = 'CargoStatus--yellow'
                }

                if (delivery_order.status.id === 23) {
                    statusClass = 'CargoStatus--green'
                }

                if (delivery_order.status.id === 21) {
                    statusClass = 'CargoStatus--blue'
                }

                if (delivery_order.status.id === 25) {
                    statusClass = 'CargoStatus--red'
                }

                const deliveredAt = delivery_order.deliveredAt ? delivery_order.deliveredAt : '-'

                const props = {
                    balloonContentHeader: `<div class="newballoon__header">${delivery_order.invoiceNumber}</div>`,
                    balloonContentBody: `<div class="newballoon__body">
                        <div class="grid grid-cols-2 gap-1 items-center">
                            <div class="body__label">Клиент: </div>
                            <div class="body__value">${delivery_order.companyName}</div>

                            <div class="body__label">Адрес получателя: </div>
                            <div class="body__value">${address}</div>

                            <div class="body__label">Курьер: </div>
                            <div class="body__value">${courier}</div>

                            <div class="body__label">Статус : </div>
                            <div class="body__value">
                                <div class="CargoStatus ${statusClass}">
                                    <div class="CargoStatus__title">${status}</div>
                                </div>
                            </div>

                            <div class="body__label">Доставлено в : </div>
                            <div class="body__value">${deliveredAt}</div>
                        </div>
                    </div>`,
                }

                const balloonContentLayout = createSingleOrderLayout(
                    props.balloonContentHeader,
                    props.balloonContentBody
                )

                const placemark = map_graphics.createPoint(
                    coordinates,
                    {
                        iconContent: delivery_order.invoiceNumber,
                        balloonContentHeader: props.balloonContentHeader,
                        balloonContentBody: props.balloonContentBody,
                    },
                    {
                        // hasBalloon: false,
                        preset: 'islands#yellowStretchyIcon',
                        balloonContentLayout,
                    }
                )

                // placemark.events.add('click', (event: any) => {
                //     openDeliveryPanel(delivery_order)
                // })

                placemarks.push(placemark)
            })

            cluster.options.set({ clusterDisableClickZoom: true })

            cluster.add(placemarks)
            addGeoObject(map_instance as ymaps.Map, cluster)
        }

        const drawSector = (sector: Sector) => {
            const polygon = map_graphics.createPolygon({
                coordinates: JSON.parse(sector.coordinates),
                color: sector.color,
                hintContent: `Сектор ${sector.name}`,
            })

            addGeoObject(map_instance as ymaps.Map, polygon)
        }

        const drawSectors = (sectors: Sector[]) => {
            if (!map_instance) return
            sectors.forEach((sector) => {
                if (sector.coordinates !== 'null') drawSector(sector)
            })
        }

        const loadSectors = async () => {
            try {
                data_state.sectors.is_loading = true

                let dispatcherSectorIds: number[] | undefined = undefined

                if (new_dispatcher_store.dispatcherSectorIds.length) {
                    dispatcherSectorIds = new_dispatcher_store.dispatcherSectorIds
                }

                const response = await sectorsAPI.getSectors({
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds,
                })

                data_state.sectors.list = response.data.data

                drawSectors(response.data.data)

                data_state.sectors.is_failed = false
            } catch (error) {
                data_state.sectors.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке секторов произошла ошибка',
                })
            } finally {
                data_state.sectors.is_loading = false
            }
        }

        const loadCouriers = async () => {
            try {
                data_state.couriers.is_loading = true

                let dispatcherSectorIds: number[] | undefined = undefined

                if (new_dispatcher_store.dispatcherSectorIds.length) {
                    dispatcherSectorIds = new_dispatcher_store.dispatcherSectorIds
                }

                const response = await couriersAPI.getCouriers({
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds,
                    statusIds: [2],
                })

                data_state.couriers.list = response.data.data

                data_state.couriers.is_failed = false
            } catch (error) {
                data_state.couriers.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке курьеров произошла ошибка',
                })
            } finally {
                data_state.couriers.is_loading = false
            }
        }

        const loadCompanies = async () => {
            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({
                    page: 1,
                    getAll: 1,
                    isExtended: 0,
                    limit: 9999,
                })
                data_state.companies.list = response.data

                data_state.companies.is_failed = false
            } catch (error) {
                data_state.companies.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка компаний произошла ошибка',
                })
            } finally {
                data_state.companies.is_loading = false
            }
        }

        const loadContainers = async () => {
            try {
                data_state.containers.is_loading = true

                const params = {
                    dateFrom: filters_state.deliveredAtFrom,
                    dateTo: filters_state.deliveredAtTo,
                }

                const response = await planningAPI.getContainers(params)
                data_state.containers.list = response.data.data

                data_state.containers.is_failed = false
            } catch (error) {
                data_state.containers.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка контейнеров произошла ошибка',
                })
            } finally {
                data_state.containers.is_loading = false
            }
        }

        const loadStatusTypes = async () => {
            try {
                data_state.status_types.is_loading = true

                const response = await statusTypeAPI.getStatusTypes({
                    page: 1,
                    limit: 9999,
                    typeId: 2,
                })

                data_state.status_types.list = response.data.data.filter(
                    (statusType) => statusType.id !== 24
                )

                data_state.status_types.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка типов статусов произошла ошибка',
                })
                data_state.status_types.is_failed = true
            } finally {
                data_state.status_types.is_loading = false
            }
        }

        const loadStatusesLO = async () => {
            try {
                data_state.statuses_lo.is_loading = true

                const response = await takeAPI.getWaitListStatuses({
                    type: 2,
                    isDispatcher: 1,
                })

                data_state.statuses_lo.list = response.data.data

                data_state.statuses_lo.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка статусов произошла ошибка',
                })
                data_state.statuses_lo.is_failed = true
            } finally {
                data_state.statuses_lo.is_loading = false
            }
        }

        const setMapCenterOnSector = (sector: Sector) => {
            const centroid = map_graphics.getPolygonCentroid(JSON.parse(sector.coordinates))
            setMapCenter(map_instance as ymaps.Map, centroid)
        }

        const onMapReady = (instance: ymaps.Map) => {
            map_instance = instance
            state.is_map_ready = true

            loadData()
        }

        const setTodayDate = () => {
            let today = new Date().toISOString().slice(0, 10)

            filters_state.deliveredAtFrom = today
            filters_state.deliveredAtTo = today
        }

        const downloadReport = async () => {
            filters$.value.$touch()
            if (filters$.value.$invalid) return

            try {
                report_state.is_loading = true

                let dispatcherSectorIds: number[] = []

                if (new_dispatcher_store.dispatcherSectorIds.length) {
                    dispatcherSectorIds = new_dispatcher_store.dispatcherSectorIds
                }

                const waitListComment = state.is_other ? 'other' : filters_state.waitListComment

                let isPickup = null as number | null

                if (filters_state.isPickup) {
                    if (filters_state.isPickup === '0') {
                        isPickup = 0
                    }

                    if (filters_state.isPickup === '1') {
                        isPickup = 1
                    }
                }

                const params = {
                    createdAtFrom: filters_state.deliveredAtFrom as string,
                    createdAtTo: filters_state.deliveredAtTo as string,
                    dispatcherSectorIds,
                    statusIds: filters_state.statusIds,
                    waitListStatusIds: filters_state.waitListStatusIds,
                    courierId: filters_state.courierId,
                    companyId: filters_state.companyId,
                    containerId: filters_state.containerId,
                    address: filters_state.address,
                    isPickup,
                    waitListComment,
                }

                const response = await deliveryAPI.downloadReport(params)
                saveFile(
                    response.data,
                    `Отчет (${filters_state.deliveredAtFrom} - ${filters_state.deliveredAtTo})`
                )
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При выгрузке отчета произошла ошибка' })
            } finally {
                report_state.is_loading = false
            }
        }

        const togglePayment = (type?: number, clientId?: number) => {
            payment_state.clientId = clientId || null
            payment_state.type = type || null
            payment_state.is_opened = !payment_state.is_opened
        }

        const savePaymentDeliveries = async (payload: FormData) => {
            try {
                payment_state.is_loading = true

                await couriers.savePaymentDeliveries(payload)

                notifications.notify({ type: 'success', title: 'Чеки успешно загружены' })
                togglePayment()
                closeInvoicePanel()
                loadData()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке чеков произошла ошибка',
                })
            } finally {
                payment_state.is_loading = false
            }
        }

        const loadComments = async () => {
            try {
                comments_state.is_loading = true

                const response = await takeAPI.getComments(2)
                comments_state.list = response.data.data
                comments_state.list.push({ text: 'Другое' })

                comments_state.is_failed = false
            } catch (error) {
                console.error(error)
                comments_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка произошла ошибка',
                })
            } finally {
                comments_state.is_loading = false
            }
        }

        const onSelectRow = (row: DeliveryOrder) => {
            const index = deliveries_state.selected_orders.indexOf(row.id)
            if (index === -1) {
                deliveries_state.selected_orders.push(row.id)
            } else {
                deliveries_state.selected_orders.splice(index, 1)
            }
        }

        const onUnselectRow = (row: DeliveryOrder) => {
            const index = deliveries_state.selected_orders.indexOf(row.id)
            deliveries_state.selected_orders.splice(index, 1)
        }

        const onUnselectAll = () => (deliveries_state.selected_orders = [])

        const onSelectAll = () => {
            onUnselectAll()
            deliveries_state.orders.forEach((order) => {
                deliveries_state.selected_orders.push(order.id)
            })
        }

        const getCheapClass = computed(() => {
            const status_id = invoice_state.invoice_status?.id
            let result = 'small font-bold '

            if (status_id) {
                if (LtlStatusColors[status_id]) result += LtlStatusColors[status_id]
                else if (status_id === 51) result += 'red'
                else if (![1, 4].includes(status_id)) result += 'yellow'
            }

            return result
        })

        const selectedInvoiceIds = computed(() => {
            const selectedIds: number[] = []
            deliveries_state.selected_orders.forEach((id) => {
                const orderFound = deliveries_state.orders.find((order) => order.id === id)
                if (orderFound) selectedIds.push(orderFound.invoiceId)
            })
            return selectedIds
        })

        const startWaitListMass = () => {
            wait_list_mass_state.is_opened = true
        }

        const cancelWaitListMass = () => {
            wait_list_mass_state.is_opened = false
            onUnselectAll()
        }

        const onCommentChange = (value: string) => {
            if (value === 'Другое') state.is_other = true
            else state.is_other = false
        }

        // New Dispatcher Sector Listener
        watch(
            () => new_dispatcher_store.dispatcherSectorIds,
            () => {
                loadData()
            }
        )

        // Dates Listener
        watch([() => filters_state.deliveredAtFrom, () => filters_state.deliveredAtTo], () => {
            loadContainers()
        })

        onBeforeMount(() => {
            setTodayDate()
            loadStatusTypes()
            loadStatusesLO()
            loadCompanies()
            loadComments()
        })

        return {
            PICKUP_OPTIONS,
            tablesStore,
            COLUMNS,
            WAIT_LIST_STATUSES,
            //
            state,
            invoice_state,
            filters_state,
            deliveries_state,
            deliveries_map_state,
            data_state,
            filters$,
            report_state,
            wait_list_state,
            wait_list_mass_state,
            mass_state,
            //
            onCommentChange,
            loadComments,
            loadSectors,
            loadCouriers,
            loadStatusTypes,
            loadStatusesLO,
            downloadReport,
            loadCompanies,
            loadContainers,
            openInvoicePanel,
            onInvoiceLoaded,
            closeInvoicePanel,
            onUnselectRow,
            onSelectRow,
            onSelectAll,
            onUnselectAll,
            startWaitListMass,
            cancelWaitListMass,
            //
            closeMap,
            openMap,
            onMapReady,
            setMapCenterOnSector,
            //
            onFiltersChanged,
            resetFilters,
            onLimitChange,
            onChangePage,
            //
            payment_state,
            togglePayment,
            savePaymentDeliveries,
            startWaitList,
            cancelWaitList,
            setWaitListStatus,
            comments_state,
            getCheapClass,
            startMassApprove,
            cancelMassApprove,
            havePermissions,
            selectedInvoiceIds,
        }
    },
})
